// La visita a la tienda, no sólo al personalizador.
//
// Hasta hoy sólo se medía lo que pasaba en una ficha con diseño: medido
// en producción el 13-sep-2026, Shopify contaba 15.510 visitas en 30
// días y nosotros veíamos 919 — el 6 %. Todo lo demás —por dónde entra
// la gente, qué mira, dónde se va sin llegar nunca a un producto
// personalizable— no existía en el panel.
//
// ── Por qué esto cabe aquí y no hizo falta tocar el tema ────────────
// La extensión de tema es un APP-EMBED: `embed.js` ya se descarga en
// TODAS las páginas de la tienda (comprobado en la home de dondefue.com,
// 13-sep-2026). Lo único que faltaba era avisar. Y el dominio de la
// tienda lo pone Shopify en `window.Shopify.shop` en cualquier página,
// así que tampoco hace falta que el tema nos lo pase: una pieza menos
// que desplegar con `shopify app deploy`, que va por su cuenta.
//
// ── Un aviso por página, no un lote ─────────────────────────────────
// Un lote con reloj —lo que hace el tracker del personalizador— aquí no
// sirve: la página se va antes de que venza. Se manda UNO por página
// vista, con `sendBeacon`, que es la primitiva que sobrevive a la
// descarga de la página.
//
// ── Privacidad ─────────────────────────────────────────────────────
// Las mismas reglas que el resto: sin cookies, sin IP, sin user-agent.
// De la UBICACIÓN se mandan ciudad, provincia y país, y sólo esos tres:
// los resuelve el tema en el navegador del cliente (ver
// `ubicacionDelCliente`) y su objeto llega con la IP, la latitud y el
// código postal delante, que se quedan donde están.
// Se manda el CAMINO de la página y NUNCA la query — que puede llevar
// lo que el cliente buscó, su correo en un enlace de recuperación o el
// token de otro. El id de sesión es el de la pestaña, muere al
// cerrarla y no cruza visitas.
//
// Y el interruptor del comercio (`enableAnalytics`) se comprueba en el
// SERVIDOR, no aquí: en una página sin personalizador no se piden los
// ajustes de la tienda, así que el cliente no sabe si están apagados.
// Es el mismo reparto que en /storefront/events.

import { sessionId, visitId, origenDeLaVisita, detectDevice } from './tracker.js';

/** Content-type CORS-safelisted: sin él la petición pide preflight y un
 *  beacon con preflight se pierde en silencio. La lección larga está en
 *  tracker.ts; aquí basta con no romperla. */
const BEACON_CONTENT_TYPE = 'text/plain;charset=UTF-8';

/** Tope del camino. Un handle de Shopify no pasa de ~150. */
const MAX_PATH = 300;
/** Tope de cada trozo de la ubicación. El topónimo más largo de España
 *  —«Villanueva de la Concepción»— mide 27. */
const MAX_LUGAR = 80;

// ── El reloj de "está dentro de la web" ─────────────────────────────
//
// Se cuenta el tiempo con la pestaña VISIBLE y alguien delante, y se
// manda en trozos. No se deduce de las marcas de tiempo de las páginas
// porque eso mide de la primera a la última y da CERO en una visita de
// una sola página — que son la mayoría.
//
// `ACTIVO_MS`: sin un gesto en este rato, el reloj se para. Una pestaña
// abierta toda la tarde en segundo plano no son cuatro horas de visita,
// y un número así no sólo es falso: arrastra la media de todo el panel.
const ACTIVO_MS = 60_000;
/** Cada cuánto se mira el reloj.
 *
 *  Un segundo, y no cuesta una petición más: el tic es local y lo único
 *  que viaja es el resumen. Con cinco, las visitas cortas caían todas en
 *  múltiplos de cinco —las dos primeras medidas en producción salieron
 *  las dos a "5 s" clavados— y eso, en un rebote, es la diferencia entre
 *  "miró y se fue" y "ni le dio tiempo a ver la página". */
const TIC_MS = 1_000;
/** Trozo mínimo que merece un envío cuando el reloj se para. */
const MINIMO_MS = 2_000;
/** Cada cuánto se manda lo acumulado. Un minuto es lo que hace posible
 *  la pregunta "¿quién está dentro AHORA?" sin convertir cada lectura
 *  larga en veinte peticiones. */
const LATIDO_MS = 60_000;
/** Hasta cuándo, desde el último gesto, se sigue avisando de que la
 *  pestaña está QUIETA. El reloj se para al minuto sin gestos y la
 *  tarjeta de «ahora mismo» suelta a esa persona cinco minutos después
 *  de la última señal: pasado eso nadie lee el aviso, y una pestaña
 *  olvidada abierta toda la tarde no puede costar una petición por
 *  minuto. */
const QUIETA_HASTA_MS = ACTIVO_MS + 5 * 60_000;

let yaAvisado = false;
/** Ms acumulados y todavía sin mandar. */
let pendienteMs = 0;
let ultimoTic = 0;
let ultimoGesto = 0;
/** Cuándo salió el último aviso, sea cual sea. Marca el compás del
 *  aviso de pestaña quieta. */
let ultimoEnvio = 0;
let reloj: ReturnType<typeof setInterval> | null = null;
/** POR QUÉ se manda, cuando no es el latido.
 *
 *  Los dos primeros son el ADIÓS y el servidor NO los toma por
 *  presencia: irse no es señal de estar, y mientras lo fue el panel
 *  dejaba a quien cerraba la pestaña «dentro de la tienda» los cinco
 *  minutos siguientes. `vuelve` es el contrario y hace falta por eso
 *  mismo: sin él, quien cambia de pestaña y regresa tardaría hasta un
 *  minuto —el latido— en volver a aparecer.
 *
 *  `quieta` no es ni una cosa ni la otra: la pestaña sigue delante y
 *  nadie la toca. El servidor tampoco lo toma por presencia; sólo sella
 *  que la página sigue ABIERTA, que es lo que deja al panel decir
 *  «inactiva hace 4 min» en vez de un «hace 4 min» que no distinguía a
 *  quien está quieto de quien se fue sin que llegara el adiós. */
type Motivo = 'oculta' | 'cierre' | 'vuelve' | 'quieta';

/** `de` fuerza a qué página pertenece el rato pendiente. Hace falta en
 *  la navegación por historial: cuando se llama, la URL ya ha cambiado
 *  y el tiempo es de la página que se acaba de dejar. */
let enviarTiempo: ((de?: string, motivo?: Motivo) => void) | null = null;

/** Dominio de la tienda. Shopify lo inyecta en todas las páginas del
 *  escaparate; fuera de la tienda (la vista de cliente del panel, el
 *  estudio 3D, un preview local) no existe, y entonces esto no hace
 *  nada — que es lo correcto: esas visitas no son de nadie. */
function dominioDeLaTienda(): string {
  try {
    const s = (window as unknown as { Shopify?: { shop?: unknown } }).Shopify?.shop;
    return typeof s === 'string' ? s : '';
  } catch {
    return '';
  }
}

/** El id de navegador de Shopify — la cookie `_shopify_y`.
 *
 *  Es el ÚNICO dato que ven los dos lados de la medición. El pago corre
 *  en un sandbox donde nuestro JavaScript no entra y donde no existen ni
 *  nuestro localStorage ni el `visitId`; lo que el Web Pixel sí trae en
 *  cada evento es este `clientId`. Leyéndolo aquí, la visita y el
 *  checkout se pueden unir sin pedirle a Shopify ningún dato de nadie.
 *
 *  No identifica a una persona: es un UUID por navegador que Shopify ya
 *  pone para su propia analítica (no lo creamos nosotros, y si el
 *  cliente rechaza esa cookie sencillamente no está y el checkout de esa
 *  visita no se podrá atar — que es lo correcto). Caduca con la misma
 *  retención que el resto del panel.
 *
 *  `document.cookie` puede lanzar en un iframe con el acceso a cookies
 *  restringido, así que va en try como todo lo de aquí. */
function idDeNavegadorDeShopify(): string {
  try {
    const m = /(?:^|;\s*)_shopify_y=([^;]+)/.exec(document.cookie);
    return m?.[1] ? decodeURIComponent(m[1]).slice(0, 64) : '';
  } catch {
    return '';
  }
}

/** DÓNDE ESTÁ LA PERSONA, según el TEMA.
 *
 *  `assets/df-geo.js` de dondefue.com resuelve la ubicación por IP
 *  contra ipapi.co DESDE EL NAVEGADOR del cliente, la cachea una hora
 *  en su localStorage y la publica en `window.dfGeo`. Se lee de ahí y
 *  no se monta una petición propia, por lo mismo que el plazo de
 *  entrega de la ficha y el desplegable de dominios del correo: cuando
 *  el tema ya lo sabe, una segunda cuenta sólo puede acabar
 *  discrepando con la suya. Y sale gratis — el cupo de ipapi que se
 *  gasta es el de la IP del visitante, no el de la tienda.
 *
 *  SE COGEN TRES CAMPOS Y NI UNO MÁS. `window.dfGeo` llega con la IP,
 *  la latitud, el código postal y el operador delante; nada de eso sale
 *  de aquí, y la regla se lee entera en esta función.
 *
 *  En un visitante nuevo llega TARDE —hay un fetch de por medio—, así
 *  que el primer aviso de la página puede salir sin ella. No se espera:
 *  el servidor se queda con el primer valor no nulo y los avisos del
 *  reloj la vuelven a mandar, exactamente como la cookie de Shopify.
 *
 *  Un tema que no la ponga deja esto vacío, y eso el panel lo lee como
 *  «no se midió» y no como «no se sabe». */
function ubicacionDelCliente(): { city?: string; region?: string; country?: string } {
  try {
    const g = (window as unknown as { dfGeo?: Record<string, unknown> }).dfGeo;
    if (!g) return {};
    const texto = (v: unknown): string | undefined => {
      if (typeof v !== 'string') return undefined;
      const t = v.trim();
      return t ? t.slice(0, MAX_LUGAR) : undefined;
    };
    // El país en ISO-3166 alfa-2 y no `country_name`, que el proveedor
    // manda en inglés («Spain»): del código, el panel saca el nombre en
    // el idioma de quien lo mira.
    const cc = texto(g['country_code']) ?? texto(g['country']);
    return {
      city: texto(g['city']),
      region: texto(g['region']),
      country: cc && /^[A-Za-z]{2}$/.test(cc) ? cc.toUpperCase() : undefined,
    };
  } catch {
    return {};
  }
}

/** La forma de una rama de test A/B, `<experimento>:<rama>`. La misma
 *  que valida el servidor: una cadena rara acabaría en un GROUP BY. */
const ETIQUETA_DE_RAMA = /^[a-z0-9-]{1,48}:[ab]$/;

/** EN QUÉ RAMA DE UN A/B ESTÁ ESTA VISITA, según el CARGADOR DEL TEMA.
 *
 *  El A/B «Teeinblue contra Brandéate» se decide en
 *  `personalizer-loader.liquid`, en el <head> y antes de que corra este
 *  bundle, porque una de las dos ramas es precisamente NO montar nada
 *  nuestro. Lo deja en `window.__dfpExperimento` y sólo si la visita
 *  CUENTA: un navegador sin memoria se lleva la rama A y no lo pone.
 *
 *  Se manda en TODOS los avisos, no sólo en el de la página donde se
 *  decide, y el servidor se queda con el primero que lo traiga
 *  (COALESCE), como la ubicación y la cookie de Shopify. Aquí además
 *  hace falta por una razón propia: la rama A es la ficha de Teeinblue
 *  y ahí el único que avisa somos nosotros, así que si ese aviso se
 *  pierde, el del reloj es el que la salva.
 *
 *  Sin rama no se manda la clave, en vez de mandarla vacía. */
function experimentoDeLaVisita(): string | undefined {
  try {
    const e = (window as unknown as { __dfpExperimento?: unknown }).__dfpExperimento;
    return typeof e === 'string' && ETIQUETA_DE_RAMA.test(e) ? e : undefined;
  } catch {
    return undefined;
  }
}

/** Base de la API: de dónde se ha descargado este mismo bundle.
 *
 *  No se lee del host del personalizador (`data-api`) porque en la
 *  mayoría de las páginas ese host no existe — que es justo el caso que
 *  esto viene a cubrir. */
function baseDeLaApi(base?: string): string {
  if (base) return base.replace(/\/$/, '');
  try {
    return new URL(import.meta.url).origin;
  } catch {
    return '';
  }
}

/** ¿Esta carga la está mirando alguien?
 *
 *  Una página PRE-RENDERIZADA por el navegador, o abierta en una
 *  pestaña de fondo que el cliente nunca llega a mirar, ejecuta el
 *  bundle igual que una visita de verdad. Contarla infla el
 *  denominador de todo el panel con gente que no existe. Y
 *  `navigator.webdriver` lo pone el navegador cuando lo conduce un
 *  programa: no caza a todos los robots, pero sí a los que ejecutan JS,
 *  que son justo los que se cuelan. */
function nadieEstaMirando(): boolean {
  try {
    const d = document as Document & { prerendering?: boolean };
    if (d.prerendering === true) return true;
    if (navigator.webdriver === true) return true;
    return document.visibilityState !== 'visible';
  } catch {
    return false;
  }
}

/** Avisa de UNA página vista. Idempotente por carga de página: llamarlo
 *  dos veces no cuenta dos.
 *
 *  Si la página todavía no se ve —pre-render, pestaña de fondo— se
 *  ESPERA a que se vea, y si nunca se ve no se cuenta nada. */
export function avisarDePagina(opts: { apiBase?: string } = {}): void {
  if (yaAvisado) return;
  const shop = dominioDeLaTienda();
  const apiBase = baseDeLaApi(opts.apiBase);
  if (!shop || !apiBase) return;

  try {
    if (navigator.webdriver === true) return;
  } catch { /* sin navigator utilizable: se sigue. */ }

  if (nadieEstaMirando()) {
    const alVerse = (): void => {
      if (document.visibilityState !== 'visible') return;
      document.removeEventListener('visibilitychange', alVerse);
      avisarDePagina(opts);
    };
    document.addEventListener('visibilitychange', alVerse);
    return;
  }

  yaAvisado = true;

  const url = `${apiBase}/storefront/pageview?shop=${encodeURIComponent(shop)}`;
  const mandar = (cuerpo: Record<string, unknown>): void => {
    try {
      const body = JSON.stringify(cuerpo);
      const blob = new Blob([body], { type: BEACON_CONTENT_TYPE });
      const ok = typeof navigator.sendBeacon === 'function'
        ? navigator.sendBeacon(url, blob)
        : false;
      if (!ok) {
        void fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': BEACON_CONTENT_TYPE },
          body,
          keepalive: true,
        }).catch(() => {});
      }
    } catch {
      // La analítica jamás propaga hacia arriba: esto corre en CADA
      // página de la tienda, así que un fallo aquí sería uno en todas.
    }
  };

  try {
    const sid = sessionId();
    const o = origenDeLaVisita();
    mandar({
      sessionId: sid,
      // La VISITA, que es lo que agrupa las páginas de un cliente
      // aunque salte de pestaña. Ver `visitId()` en tracker.ts.
      visitId: visitId(),
      // El puente con el checkout. Ver `idDeNavegadorDeShopify`.
      shopifyClientId: idDeNavegadorDeShopify() || undefined,
      // Sin query, a propósito. Ver la nota de privacidad de arriba.
      path: window.location.pathname.slice(0, MAX_PATH),
      device: detectDevice(),
      locale: navigator.language || undefined,
      referrerHost: o.referrerHost,
      utmSource: o.utmSource,
      utmMedium: o.utmMedium,
      utmCampaign: o.utmCampaign,
      // De dónde ES, que es la otra mitad de «de dónde viene».
      ...ubicacionDelCliente(),
      // La rama del A/B del cargador del tema, si la hay.
      experiment: experimentoDeLaVisita(),
    });
    arrancarReloj(sid, mandar);
    vigilarNavegacion(sid, mandar);
  } catch { /* ver `mandar`: esto no puede tumbar la página. */ }
}

/** Cuenta las páginas que el tema pinta SIN recargar.
 *
 *  Muchos temas filtran una colección, cambian de variante o abren un
 *  producto rápido con `history.pushState`, y eso es una página vista
 *  que no dispara ninguna carga. Sin esto, una visita que recorre media
 *  tienda filtrando salía como una sola página.
 *
 *  Sólo cuenta cuando cambia el CAMINO. La query se mueve sola todo el
 *  rato —el selector de variante del tema reescribe `?variant=`, y
 *  nuestro propio sellado del enlace de diseño hace `replaceState` tras
 *  cada guardado—, así que contar cambios de query llenaría la tabla de
 *  páginas fantasma. */
function vigilarNavegacion(sid: string, mandar: (c: Record<string, unknown>) => void): void {
  let ultimo = window.location.pathname;

  const mirar = (): void => {
    const ahora = window.location.pathname;
    if (ahora === ultimo) return;
    // El tiempo pendiente pertenece a la página que se DEJA, y para
    // cuando esto corre la URL YA ha cambiado: se le pasa a mano.
    enviarTiempo?.(ultimo);
    ultimo = ahora;
    const o = origenDeLaVisita();
    mandar({
      sessionId: sid,
      visitId: visitId(),
      shopifyClientId: idDeNavegadorDeShopify() || undefined,
      path: ahora.slice(0, MAX_PATH),
      device: detectDevice(),
      locale: navigator.language || undefined,
      referrerHost: o.referrerHost,
      utmSource: o.utmSource,
      utmMedium: o.utmMedium,
      utmCampaign: o.utmCampaign,
      // De dónde ES, que es la otra mitad de «de dónde viene».
      ...ubicacionDelCliente(),
      experiment: experimentoDeLaVisita(),
    });
  };

  window.addEventListener('popstate', mirar);
  // `pushState` y `replaceState` no disparan ningún evento, así que hay
  // que envolverlos. Se llama SIEMPRE al original primero y el nuestro
  // va en un try: una analítica no puede romper la navegación del tema.
  for (const nombre of ['pushState', 'replaceState'] as const) {
    const original = history[nombre] as unknown as ((...a: unknown[]) => unknown) & {
      __dfpEnvuelto?: boolean;
    };
    if (typeof original !== 'function') continue;
    // Envolver DOS veces cuenta cada navegación dos veces. No es
    // hipotético: pasa en cuanto dos copias del bundle conviven en la
    // misma página —una caché vieja y la nueva— y es justo el tipo de
    // fallo que sólo se ve en el recuento, nunca en un error.
    if (original.__dfpEnvuelto) continue;
    const envuelto = function envuelto(this: History, ...args: unknown[]) {
      const r = original.apply(this, args);
      try { mirar(); } catch { /* nunca hacia arriba. */ }
      return r;
    } as typeof original;
    envuelto.__dfpEnvuelto = true;
    history[nombre] = envuelto as unknown as History[typeof nombre];
  }
}

/** Pone en marcha el reloj de "dentro de la web" y lo va mandando.
 *
 *  Tres momentos de envío, y cada uno cubre un agujero del anterior:
 *   · el LATIDO, para que una lectura larga no se pierda si el navegador
 *     nunca dispara el cierre — y para saber quién sigue dentro;
 *   · al OCULTARSE la pestaña, que en iOS es lo único que llega cuando
 *     el cliente cambia de app;
 *   · en `pagehide`, que cubre el bfcache y la navegación normal.
 *
 *  Los dos últimos van MARCADOS como adiós (`motivo`), porque el
 *  servidor no puede tomarlos por presencia: mientras los tomó, cerrar
 *  la pestaña dejaba a esa persona «dentro de la tienda» cinco minutos
 *  más. Y por simetría se avisa también de la VUELTA. */
function arrancarReloj(sid: string, mandar: (c: Record<string, unknown>) => void): void {
  ultimoTic = Date.now();
  ultimoGesto = ultimoTic;
  ultimoEnvio = ultimoTic;

  const gesto = (): void => { ultimoGesto = Date.now(); };
  // `mousemove` cuenta como presencia y hace falta: en un escritorio se
  // puede leer una página larga un buen rato sin pulsar ni desplazar
  // nada, y sin esto el reloj se paraba a los sesenta segundos con
  // alguien delante leyendo. Sólo apunta una marca de tiempo, y va
  // pasivo como los demás: esto corre en todas las páginas de la tienda
  // y no puede estorbar al desplazamiento.
  for (const ev of ['pointerdown', 'keydown', 'scroll', 'touchstart', 'mousemove'] as const) {
    window.addEventListener(ev, gesto, { passive: true });
  }

  /** Suma el trozo transcurrido y dice si se está contando. */
  const acumular = (): boolean => {
    const ahora = Date.now();
    const dt = ahora - ultimoTic;
    ultimoTic = ahora;
    // Sólo cuenta si la pestaña está delante Y hubo un gesto reciente.
    const contando = document.visibilityState === 'visible'
      && ahora - ultimoGesto < ACTIVO_MS;
    // El techo descarta el salto que deja un portátil al volver de
    // suspenderse: ahí no había nadie mirando. Con el tic de un segundo
    // hace falta margen para un hilo ocupado, así que son 5 s y no 3 tics.
    if (contando && dt > 0 && dt < 5_000) pendienteMs += dt;
    return contando;
  };

  enviarTiempo = (de?: string, motivo?: Motivo): void => {
    acumular();
    // Un aviso CON motivo se manda aunque no haya rato que contar: lo
    // que informa es el hecho —se fue, o volvió—, no los milisegundos.
    // Sin esto, quien entra y se va en menos de un segundo no avisaría
    // nunca de que se ha ido, que es justo el caso más común.
    if (pendienteMs < 1000 && !motivo) return;
    // Se manda el TROZO, no el total: el servidor suma. Mandar el total
    // obligaría a que el último aviso llegara siempre, y es justo el que
    // más se pierde.
    //
    // Y con la PÁGINA a la que pertenece ese rato. No cuesta una
    // petición más —el envío ya existía— y es lo que permite contestar
    // "cuánto se queda la gente en cada página" en vez de sólo "cuánto
    // en la tienda". Se lee ahora y no al arrancar el reloj porque un
    // tema con navegación por historial cambia la ruta sin recargar.
    mandar({
      sessionId: sid,
      visitId: visitId(),
      // También aquí, y es el envío que MÁS veces la pilla: la cookie
      // de Shopify la escribe su propio script, que en un visitante
      // nuevo puede llegar después que nosotros. El servidor se queda
      // con el primer valor no nulo, así que llegar tarde no estorba.
      shopifyClientId: idDeNavegadorDeShopify() || undefined,
      engagedMs: Math.round(pendienteMs),
      engagedPath: (de ?? window.location.pathname).slice(0, MAX_PATH),
      // El motivo, para que el servidor sepa si esto es una señal de
      // ESTAR o de IRSE. Va aquí y no en una ruta propia por lo mismo
      // que el trozo de tiempo: la petición ya existía.
      motivo,
      // Y la ubicación, por la MISMA razón que la línea de arriba: en un
      // visitante nuevo el tema la resuelve con un fetch, así que el
      // aviso de la página suele salir antes de que exista. Éste llega
      // después y la pilla.
      ...ubicacionDelCliente(),
      // Y la rama, por lo mismo: ver `experimentoDeLaVisita`.
      experiment: experimentoDeLaVisita(),
    });
    pendienteMs = 0;
    ultimoEnvio = Date.now();
  };

  const tic = (): void => {
    const contando = acumular();
    const ahora = Date.now();
    // Se manda cuando toca el latido O cuando el reloj acaba de PARARSE
    // con algo pendiente. Lo segundo no es un adorno: quien se queda
    // quieto leyendo deja de sumar antes de llegar al minuto, y si el
    // navegador se lleva la pestaña sin disparar el cierre —pasa en
    // móvil— ese rato se perdía entero.
    if (pendienteMs >= LATIDO_MS || (!contando && pendienteMs >= MINIMO_MS)) {
      enviarTiempo?.();
    } else if (
      // QUIETA: delante, sin gestos, y un minuto sin decir nada. Sólo
      // mientras la tarjeta puede seguir enseñando a esta persona (ver
      // `QUIETA_HASTA_MS`). Una pestaña oculta no entra: ya avisó de
      // que se iba.
      !contando
      && document.visibilityState === 'visible'
      && ahora - ultimoGesto < QUIETA_HASTA_MS
      && ahora - ultimoEnvio >= LATIDO_MS
    ) {
      enviarTiempo?.(undefined, 'quieta');
    }
  };
  reloj = setInterval(tic, TIC_MS);
  // Que el reloj no mantenga viva una pestaña que el navegador quiere
  // dormir no aplica en el navegador, pero sí evita trabajo: si la
  // pestaña se oculta, `acumular` deja de sumar por su cuenta.

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') enviarTiempo?.(undefined, 'oculta');
    else {
      ultimoTic = Date.now();
      ultimoGesto = Date.now();
      // El `pagehide` para el reloj, y de una página en el bfcache se
      // vuelve: sin re-armarlo, el aviso de vuelta de aquí abajo
      // metería a esa persona en la tarjeta de «ahora mismo» sin que
      // nada volviera a dar señales de ella. Un aviso de presencia
      // obliga a sostenerlo.
      if (!reloj) reloj = setInterval(tic, TIC_MS);
      // Y se avisa de la VUELTA. En móvil esto es cambiar de app y
      // volver, que es constante: sin este aviso la persona estaría
      // dentro de la tienda y fuera de la tarjeta hasta el latido.
      enviarTiempo?.(undefined, 'vuelve');
    }
  });
  window.addEventListener('pagehide', () => {
    enviarTiempo?.(undefined, 'cierre');
    if (reloj) { clearInterval(reloj); reloj = null; }
  });
}
